import React, { useState } from 'react';
import {
  Container, TextField, Button, Typography, Box, Select, MenuItem, InputLabel,
  FormControl, Grid, Paper, useTheme, alpha, IconButton, CircularProgress, Divider
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AuctionLaneSelect from './AuctionLaneSelect';
import PurchaserSelect from './PurchaserSelect';

const formatDate = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 2; year >= 1980; year--) {
    years.push(year);
  }
  return years;
};

function AddCar() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    vin: '',
    make: '',
    model: '',
    trim: '',
    year: new Date().getFullYear().toString(),
    mileage: '',
    color: '',
    purchase_price: '',
    purchaser: '',
    auction_lane: '',
    purchase_date: null,
    title_received: 'na',
    inspection_received: 'no',
    posted_online: false,
    sale_type: 'dealer',
  });
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [vinDecoding, setVinDecoding] = useState(false);

  const handleDateChange = (date) => {
    setFormData({ ...formData, purchase_date: date });
    if (formErrors.purchase_date) {
        setFormErrors(prev => ({ ...prev, purchase_date: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const processedValue = name === 'posted_online' ? (value === 'true' || value === true) : value;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : processedValue,
    });

    if (formErrors[name]) {
        setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
      const errors = {};
      if (!formData.vin) errors.vin = "VIN is required";
      if (!formData.year) errors.year = "Year is required";
      if (!formData.make) errors.make = "Make is required";
      if (!formData.model) errors.model = "Model is required";
      if (formData.mileage === '' || formData.mileage === null || formData.mileage === undefined) errors.mileage = "Mileage is required";
      else if (isNaN(Number(String(formData.mileage).replace(/,/g, '')))) errors.mileage = "Mileage must be a number";
      if (!formData.color) errors.color = "Color is required";
      if (formData.purchase_price === '' || formData.purchase_price === null || formData.purchase_price === undefined) errors.purchase_price = "Purchase Price is required";
      else if (isNaN(Number(String(formData.purchase_price).replace(/,/g, '')))) errors.purchase_price = "Price must be a number";
      if (!formData.purchase_date) errors.purchase_date = "Purchase Date is required";
      if (!formData.purchaser) errors.purchaser = "Purchaser/Source is required";
      if (!formData.title_received) errors.title_received = "Title status is required";
      if (!formData.inspection_received) errors.inspection_received = "Inspection status is required";
      if (formData.posted_online === undefined || formData.posted_online === null) errors.posted_online = "Online status is required";
      if (formData.sale_type === undefined || formData.sale_type === null) errors.sale_type = "Sale type is required";

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setFormErrors({});

    if (!validateForm()) {
        setMessage('Please correct the errors highlighted below.');
        const firstErrorKey = Object.keys(formErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);
        if (errorElement) {
            errorElement.focus({ preventScroll: true });
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
    }

    setIsSubmitting(true);

    const username = localStorage.getItem('username');
    if (!username || username === 'undefined') {
      setMessage('User ID is missing or invalid. Please log in again.');
      setIsSubmitting(false);
      return;
    }

    const vehicleData = {
      ...formData,
      purchase_date: formatDate(formData.purchase_date),
      username,
      year: parseInt(formData.year, 10),
      mileage: formData.mileage !== '' && formData.mileage !== null ? parseInt(String(formData.mileage).replace(/,/g, ''), 10) : null,
      purchase_price: formData.purchase_price !== '' && formData.purchase_price !== null ? parseFloat(String(formData.purchase_price).replace(/,/g, '')) : null,
      posted_online: formData.posted_online === true || String(formData.posted_online).toLowerCase() === 'true',
    };

    Object.keys(vehicleData).forEach(key => {
      if (vehicleData[key] === '' || vehicleData[key] === null || vehicleData[key] === undefined) {
         if (!((key === 'mileage' || key === 'purchase_price') && vehicleData[key] === 0)) {
             if(!(key === 'posted_online' && vehicleData[key] === false)) {
                delete vehicleData[key];
             }
         }
      }
    });

    try {
      console.log("Submitting data:", JSON.stringify(vehicleData, null, 2));
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/insert_vehicle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(vehicleData),
      });

      if (response.ok) {
        localStorage.setItem('showAddSuccess', 'true');
        navigate('/inventory');
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to add vehicle. Server returned an error.' }));
        setMessage(errorData.message || `Error: ${response.status} ${response.statusText}`);
        console.error("Submission error response:", errorData);
      }
    } catch (error) {
      console.error('Network or Submission Error:', error);
      setMessage(error.message || 'An unexpected network error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const decodeVinWithBackend = async (vin) => {
    setVinDecoding(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/decode_vin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ vin }),
      });
      if (response.ok) {
        const decoded = await response.json();
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(decoded).filter(([k, v]) =>
              ["make", "model", "year", "trim", "color"].includes(k) && v
            )
          )
        }));
      }
    } catch (err) {
      // Optionally handle error
    } finally {
      setVinDecoding(false);
    }
  };

  const handleVinBlur = async (e) => {
    const vin = e.target.value.trim();
    if (vin.length === 17) {
      await decodeVinWithBackend(vin);
    }
  };

  const commonProps = (name) => ({
      variant: "outlined",
      size: "small",
      fullWidth: true,
      error: !!formErrors[name],
      helperText: formErrors[name] || '',
      onChange: handleChange,
      name: name,
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 3, sm: 4 } }}>
          <IconButton onClick={() => navigate('/inventory')} sx={{ mr: 1.5, bgcolor: alpha(theme.palette.grey[500], 0.08) }}>
            <ArrowBackIcon />
          </IconButton>
          <DirectionsCarIcon sx={{ fontSize: 30, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'medium' }}>
            Add New Vehicle
          </Typography>
        </Box>

        <Paper
          elevation={2}
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            p: { xs: 2, sm: 4 },
            borderRadius: theme.shape.borderRadius * 1.5,
          }}
        >
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
            Vehicle Identification
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Box sx={{ width: '100%' }}>
                <TextField
                  label="VIN"
                  value={formData.vin}
                  required
                  {...commonProps('vin')}
                  sx={{ maxWidth: 300 }}
                  onBlur={handleVinBlur}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (formData.vin.trim().length === 17) {
                        await decodeVinWithBackend(formData.vin.trim());
                      }
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <>
                        {vinDecoding ? (
                          <CircularProgress size={18} />
                        ) : (
                          <IconButton
                            aria-label="decode vin"
                            edge="end"
                            onClick={async () => {
                              if (formData.vin.trim().length === 17) {
                                await decodeVinWithBackend(formData.vin.trim());
                              }
                            }}
                            disabled={vinDecoding}
                            size="small"
                          >
                            <SearchIcon />
                          </IconButton>
                        )}
                      </>
                    )
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    ml: 0.5,
                    color: 'text.secondary',
                    display: 'block',
                    maxWidth: 300
                  }}
                >
                  Press the search icon to autofill vehicle information
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl variant="outlined" size="small" fullWidth required error={!!formErrors.year}>
                <InputLabel>Year</InputLabel>
                <Select name="year" value={formData.year} label="Year" onChange={handleChange}>
                  {generateYearOptions().map(year => <MenuItem key={year} value={year.toString()}>{year}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Make" value={formData.make} required {...commonProps('make')} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Model" value={formData.model} required {...commonProps('model')} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label="Trim (Optional)" value={formData.trim} {...commonProps('trim')} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
            Specifications
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Mileage"
                type="text"
                inputMode='numeric'
                value={formData.mileage}
                required
                {...commonProps('mileage')}
                sx={{ maxWidth: 160 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Color"
                value={formData.color}
                required
                {...commonProps('color')}
                sx={{ maxWidth: 160 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
            Purchase Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Purchase Price"
                type="text"
                inputMode='decimal'
                value={formData.purchase_price}
                required
                InputProps={{ startAdornment: <Box component="span" sx={{ mr: 0.5, color: theme.palette.text.secondary }}>$</Box> }}
                {...commonProps('purchase_price')}
                sx={{ maxWidth: 160 }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <DatePicker
                selected={formData.purchase_date}
                onChange={handleDateChange}
                dateFormat="MM/dd/yyyy"
                placeholderText="Select Purchase Date"
                required
                customInput={
                  <TextField
                    label="Purchase Date"
                    required
                    value={formData.purchase_date ? formatDate(formData.purchase_date) : ''}
                    {...commonProps('purchase_date')}
                    helperText={null}
                    sx={{ maxWidth: 160 }}
                  />
                }
                popperPlacement="bottom-end"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PurchaserSelect
                value={formData.purchaser}
                onChange={handleChange}
                username={localStorage.getItem('username')}
                required
                error={!!formErrors.purchaser}
                helperText={formErrors.purchaser || ''}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      transition: 'border-color 0.3s',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AuctionLaneSelect
                value={formData.auction_lane}
                onChange={handleChange}
                username={localStorage.getItem('username')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                      transition: 'border-color 0.3s',
                    },
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth required error={!!formErrors.sale_type}>
                <InputLabel>Fund Source</InputLabel>
                <Select
                  name="sale_type"
                  value={formData.sale_type || 'dealer'}
                  label="Fund Source"
                  onChange={handleChange}
                >
                  <MenuItem value="dealer">Dealer Owned</MenuItem>
                  <MenuItem value="floor">Floor Plan</MenuItem>
                  <MenuItem value="consignment">Consignment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
            Status & Documentation
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth required error={!!formErrors.title_received}>
                <InputLabel>Title Received?</InputLabel>
                <Select name="title_received" value={formData.title_received} label="Title Received?" onChange={handleChange}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                  <MenuItem value="na">N/A</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth required error={!!formErrors.inspection_received}>
                <InputLabel>Inspection Done?</InputLabel>
                <Select name="inspection_received" value={formData.inspection_received} label="Inspection Done?" onChange={handleChange}>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl variant="outlined" size="small" fullWidth required error={!!formErrors.posted_online}>
                <InputLabel>Posted Online?</InputLabel>
                <Select name="posted_online" value={String(formData.posted_online)} label="Posted Online?" onChange={handleChange}>
                  <MenuItem value={'true'}>Yes, Posted</MenuItem>
                  <MenuItem value={'false'}>Not Posted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            {message && (
              <Typography
                color={message.includes('Failed') || message.includes('error') || message.includes('correct the errors') ? "error" : "success"}
                sx={{ mb: 2, fontWeight: 'medium' }}
              >
                {message}
              </Typography>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              size="large"
              sx={{
                minWidth: '220px',
                py: 1.2,
                textTransform: 'none'
              }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Add Vehicle to Inventory'}
            </Button>
          </Box>
        </Paper>
      </Container>
    </motion.div>
  );
}

export default AddCar;